import React, { createContext, useContext, useState, useCallback } from 'react';

export interface HeaderSearchResult {
  id: number;
  label: string;
  sub?: string;
  type: 'candidate' | 'actor' | 'election';
  extra?: string;
  path: string;
}

// Lets the page currently mounted under Layout (e.g. Networks) claim a header search
// selection for itself — e.g. jump to a node in a visualization — instead of AppHeader's
// default route navigation. Return true from the handler if you handled the selection.
type SearchInterceptHandler = (result: HeaderSearchResult) => boolean;

interface SearchInterceptContextType {
  handler: SearchInterceptHandler | null;
  setHandler: (fn: SearchInterceptHandler | null) => void;
}

const SearchInterceptContext = createContext<SearchInterceptContextType>({
  handler: null,
  setHandler: () => {},
});

export const SearchInterceptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [handler, setHandlerState] = useState<SearchInterceptHandler | null>(null);
  // Wrap in a function so React's useState doesn't treat the stored function as an updater
  const setHandler = useCallback((fn: SearchInterceptHandler | null) => setHandlerState(() => fn), []);

  return (
    <SearchInterceptContext.Provider value={{ handler, setHandler }}>
      {children}
    </SearchInterceptContext.Provider>
  );
};

export const useSearchIntercept = () => useContext(SearchInterceptContext);
